import {useEffect, useRef, useState} from 'react';

import {Box, ButtonBase, InputBase, Stack, Typography} from '@mui/material';

import {resolveVars} from '../../contentGenerator/formulaUtils';
import {Spinner} from '../../contentGenerator/components/ui';
import {C, TYPE_CONFIG} from '../constants';
import {useReviewAI} from '../hooks/useReviewAI';
import {uid} from '../utils';

const DEFAULT_SUGGESTIONS = [
    'Make this more concise',
    'Add a stronger hook',
    'Make it feel more premium',
    'Use more specific language',
    'Try a different angle',
];

export default function ChatPanel({
    apiKey,
    brandContext = '',
    charLimit = 0,
    customActions = [],
    field,
    inputImages = [],
    listing,
    messages,
    onClose,
    onMessage,
    onSetCustomActions = () => {},
    onUpdateField,
    profileSettings = null,
    vars = [],
}) {
    const {chatAboutField} = useReviewAI({profileSettings, brandContext});
    const [input, setInput] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [addingSuggestion, setAddingSuggestion] = useState(false);
    const [newSuggestion, setNewSuggestion] = useState('');
    const endRef = useRef(null);
    const inputRef = useRef(null);
    const newSuggestionRef = useRef(null);
    const suggestions = [...DEFAULT_SUGGESTIONS, ...customActions];
    const typeConfig = TYPE_CONFIG[field.type] || TYPE_CONFIG.bullet;
    const canSend = Boolean(input.trim()) && !loading;

    // Var autocomplete state
    const [varQuery, setVarQuery] = useState('');
    const [varMenuOpen, setVarMenuOpen] = useState(false);
    const [varMenuIndex, setVarMenuIndex] = useState(0);
    const varMenuRef = useRef(null);

    const filteredVars = varQuery
        ? vars.filter((v) => v.name.toLowerCase().includes(varQuery.toLowerCase()))
        : vars;

    const getVarTrigger = (text, cursorPos) => {
        const before = text.slice(0, cursorPos);
        const match = before.match(/\{\{([a-zA-Z0-9_]*)$/);
        return match ? match[1] : null;
    };

    const insertVar = (varName) => {
        const el = inputRef.current?.querySelector?.('textarea') || inputRef.current;
        const cursorPos = el?.selectionStart ?? input.length;
        const before = input.slice(0, cursorPos);
        const after = input.slice(cursorPos);
        const triggerMatch = before.match(/\{\{([a-zA-Z0-9_]*)$/);
        if (!triggerMatch) return;
        const prefix = before.slice(0, triggerMatch.index);
        setInput(`${prefix}{{${varName}}}${after}`);
        setVarMenuOpen(false);
        setVarQuery('');
        setTimeout(() => {
            const nextPos = prefix.length + varName.length + 4;
            if (el) { el.focus(); el.setSelectionRange(nextPos, nextPos); }
        }, 0);
    };

    const scrollVarMenuTo = (index) => {
        const container = varMenuRef.current?.querySelector('[data-var-list]');
        const item = container?.children[index];
        if (item) item.scrollIntoView({block: 'nearest'});
    };

    const handleInputChange = (event) => {
        const val = event.target.value;
        setInput(val);
        const el = event.target;
        const cursorPos = el.selectionStart ?? val.length;
        const query = getVarTrigger(val, cursorPos);
        if (query !== null && vars.length) {
            setVarQuery(query);
            setVarMenuOpen(true);
            setVarMenuIndex(0);
        } else {
            setVarMenuOpen(false);
        }
    };

    const handleInputKeyDown = (event) => {
        if (varMenuOpen && filteredVars.length) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setVarMenuIndex((i) => {
                    const next = Math.min(i + 1, filteredVars.length - 1);
                    scrollVarMenuTo(next);
                    return next;
                });
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setVarMenuIndex((i) => {
                    const next = Math.max(i - 1, 0);
                    scrollVarMenuTo(next);
                    return next;
                });
                return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                insertVar(filteredVars[varMenuIndex].name);
                return;
            }
            if (event.key === 'Escape') {
                setVarMenuOpen(false);
                return;
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
        }
    };

    useEffect(() => {
        inputRef.current?.focus();
    }, [field.id]);

    useEffect(() => {
        if (typeof endRef.current?.scrollIntoView === 'function') {
            endRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }, [loading, messages]);

    const send = async (nextText = input) => {
        const raw = nextText.trim();
        if (!raw || loading) return;
        // Resolve {{var}} templates before sending
        const text = raw.includes('{{') && vars.length ? resolveVars(raw, vars) : raw;

        setLoading(true);
        setError('');
        setInput('');

        const userMessage = {id: uid(), role: 'user', content: text};
        onMessage(field.id, userMessage);

        try {
            const nextResponse = await chatAboutField({
                apiKey,
                charLimit,
                listing,
                field,
                inputImages,
                vars,
                messages: [...messages, userMessage].map((message) => ({
                    role: message.role === 'assistant' ? 'assistant' : 'user',
                    content:
                        message.role === 'assistant'
                            ? JSON.stringify({
                                  reply: message.reply || '',
                                  ...(message.updatedText ? {updatedText: message.updatedText} : {}),
                              })
                            : message.content,
                })),
            });
            const assistantMessage = {
                id: uid(),
                role: 'assistant',
                reply: nextResponse.reply || '',
                updatedText: nextResponse.updatedText || '',
            };
            onMessage(field.id, assistantMessage);

            if (assistantMessage.updatedText) {
                onUpdateField(field.id, {
                    text: assistantMessage.updatedText,
                    status: 'revised',
                    history: [...field.history, field.text].filter(Boolean),
                });
            }
        } catch (chatError) {
            const nextError = chatError.message?.includes('not configured')
                ? 'AI service issue — the backend is missing its OpenAI configuration.'
                : chatError.message?.includes('timed out')
                  ? 'Request timed out. The AI service may be slow — try again shortly.'
                  : chatError.message || 'Could not process the chat request. Check your connection and try again.';
            setError(nextError);
            onMessage(field.id, {id: uid(), role: 'error', content: nextError});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack
            direction="column"
            sx={{
                height: '100%',
                minHeight: 0,
                background: C.surface,
                overflow: 'hidden',
            }}>
            <Box sx={{padding: '12px 14px', borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt}}>
                <Stack direction="row" alignItems="center" gap="8px" sx={{marginBottom: '8px'}}>
                    <Box
                        component="span"
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: typeConfig.color,
                            display: 'block',
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: typeConfig.color,
                                textTransform: 'uppercase',
                                letterSpacing: '.07em',
                            }}>
                            {field.label}
                        </Typography>
                        <Typography sx={{fontSize: 10, color: C.textSoft}}>{listing.name}</Typography>
                    </Box>
                    <ButtonBase
                        onClick={onClose}
                        aria-label="Close chat"
                        sx={{
                            border: 'none',
                            background: 'transparent',
                            color: C.textSoft,
                            fontSize: 18,
                            lineHeight: 1,
                            cursor: 'pointer',
                        }}>
                        ×
                    </ButtonBase>
                </Stack>
                <Box
                    sx={{
                        border: `1px solid ${typeConfig.color}22`,
                        background: `${typeConfig.light}99`,
                        borderRadius: '10px',
                        padding: '8px 10px',
                    }}>
                    <Typography
                        sx={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: typeConfig.color,
                            textTransform: 'uppercase',
                            letterSpacing: '.07em',
                            marginBottom: '4px',
                        }}>
                        Current content
                    </Typography>
                    <Box
                        sx={{
                            maxHeight: field.type === 'description' ? 118 : 88,
                            overflowY: 'auto',
                            paddingRight: '4px',
                        }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: field.text ? C.ink : C.textSoft,
                                lineHeight: 1.5,
                                fontStyle: field.text ? 'normal' : 'italic',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}>
                            {field.text || 'Empty'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    padding: '14px',
                    background: `linear-gradient(180deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
                }}>
                <Stack
                    direction="column"
                    gap="10px"
                    sx={{
                        minHeight: '100%',
                        justifyContent: messages.length || loading ? 'flex-end' : 'center',
                    }}>
                    {!messages.length && !loading && (
                        <Box
                            sx={{
                                border: `1px dashed ${C.border2}`,
                                background: C.surface,
                                borderRadius: '12px',
                                padding: '14px',
                            }}>
                            <Typography sx={{fontSize: 12, color: C.textSoft, lineHeight: 1.65, marginBottom: '10px'}}>
                                Ask for refinements or use one of the quick actions below. Generated edits apply to the field immediately and remain undoable.
                            </Typography>
                            <Stack direction="column" gap="8px">
                                {suggestions.map((suggestion, index) => (
                                    <Stack key={suggestion} direction="row" gap="6px" alignItems="center">
                                        <ButtonBase
                                            aria-label={`Suggest: ${suggestion}`}
                                            onClick={() => send(suggestion)}
                                            disabled={loading}
                                            sx={{
                                                flex: 1,
                                                justifyContent: 'flex-start',
                                                border: `1px solid ${C.border}`,
                                                background: C.surfaceAlt,
                                                color: C.ink,
                                                borderRadius: '10px',
                                                padding: '10px 12px',
                                                textAlign: 'left',
                                                fontFamily: 'inherit',
                                                fontSize: 12,
                                                lineHeight: 1.4,
                                                cursor: 'pointer',
                                                opacity: loading ? 0.5 : 1,
                                            }}>
                                            {suggestion}
                                        </ButtonBase>
                                        {index >= DEFAULT_SUGGESTIONS.length && (
                                            <ButtonBase
                                                onClick={() =>
                                                    onSetCustomActions((current) =>
                                                        current.filter((s) => s !== suggestion),
                                                    )
                                                }
                                                aria-label={`Remove ${suggestion}`}
                                                sx={{
                                                    border: 'none',
                                                    background: 'transparent',
                                                    color: C.textSoft,
                                                    fontSize: 14,
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                    width: 22,
                                                    height: 22,
                                                }}>
                                                ×
                                            </ButtonBase>
                                        )}
                                    </Stack>
                                ))}
                                {addingSuggestion ? (
                                    <Stack direction="row" gap="6px">
                                        <InputBase
                                            inputRef={newSuggestionRef}
                                            autoFocus
                                            value={newSuggestion}
                                            onChange={(event) => setNewSuggestion(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' && newSuggestion.trim()) {
                                                    onSetCustomActions((current) => [
                                                        ...current,
                                                        newSuggestion.trim(),
                                                    ]);
                                                    setNewSuggestion('');
                                                    setAddingSuggestion(false);
                                                } else if (event.key === 'Escape') {
                                                    setNewSuggestion('');
                                                    setAddingSuggestion(false);
                                                }
                                            }}
                                            placeholder="Custom quick action..."
                                            sx={{
                                                flex: 1,
                                                fontFamily: 'inherit',
                                                fontSize: 12,
                                                padding: '8px 10px',
                                                border: `1px solid ${C.border2}`,
                                                borderRadius: '10px',
                                                background: C.surface,
                                                color: C.ink,
                                            }}
                                        />
                                        <ButtonBase
                                            onClick={() => {
                                                if (newSuggestion.trim()) {
                                                    onSetCustomActions((current) => [
                                                        ...current,
                                                        newSuggestion.trim(),
                                                    ]);
                                                }
                                                setNewSuggestion('');
                                                setAddingSuggestion(false);
                                            }}
                                            sx={{
                                                border: `1px solid ${C.teal}33`,
                                                background: C.tealL,
                                                color: C.teal,
                                                borderRadius: '10px',
                                                padding: '0 12px',
                                                fontFamily: 'inherit',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                            }}>
                                            Add
                                        </ButtonBase>
                                    </Stack>
                                ) : (
                                    <ButtonBase
                                        onClick={() => setAddingSuggestion(true)}
                                        sx={{
                                            justifyContent: 'center',
                                            border: `1px dashed ${C.border2}`,
                                            background: 'transparent',
                                            color: C.textSoft,
                                            borderRadius: '10px',
                                            padding: '9px 12px',
                                            fontFamily: 'inherit',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}>
                                        + Add custom action
                                    </ButtonBase>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {messages.map((message) => {
                    if (message.role === 'user') {
                        return (
                            <Stack key={message.id} direction="row" justifyContent="flex-end">
                                <Typography
                                    sx={{
                                        maxWidth: '85%',
                                        background: C.ink,
                                        color: C.surface,
                                        borderRadius: '12px 12px 4px 12px',
                                        padding: '9px 12px',
                                        fontSize: 13,
                                        lineHeight: 1.55,
                                    }}>
                                    {message.content}
                                </Typography>
                            </Stack>
                        );
                    }

                    if (message.role === 'assistant') {
                        return (
                            <Stack key={message.id} direction="column" gap="6px">
                                <Typography
                                    sx={{
                                        border: `1px solid ${C.border}`,
                                        background: C.surfaceAlt,
                                        borderRadius: '4px 12px 12px 12px',
                                        padding: '10px 12px',
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                    }}>
                                    {message.reply || 'Updated.'}
                                </Typography>
                                {!!message.updatedText && (
                                    <Typography
                                        sx={{
                                            border: `1px solid ${C.teal}33`,
                                            background: C.tealL,
                                            color: C.teal,
                                            borderRadius: '10px',
                                            padding: '7px 10px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                        }}>
                                        Content updated
                                    </Typography>
                                )}
                            </Stack>
                        );
                    }

                    return (
                        <Typography
                            key={message.id}
                            sx={{
                                border: `1px solid ${C.coral}33`,
                                background: C.coralL,
                                color: C.coral,
                                borderRadius: '10px',
                                padding: '8px 10px',
                                fontSize: 11,
                            }}>
                            {message.content}
                        </Typography>
                    );
                    })}

                    {loading && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap="8px"
                            sx={{
                                border: `1px solid ${C.border}`,
                                background: C.surface,
                                borderRadius: '4px 12px 12px 12px',
                                padding: '10px 12px',
                                display: 'inline-flex',
                                fontSize: 12,
                                color: C.textSoft,
                            }}>
                            <Spinner dark />
                            Thinking
                        </Stack>
                    )}
                    <div ref={endRef} />
                </Stack>
            </Box>

            <Box sx={{padding: '12px 14px', borderTop: `1px solid ${C.border}`, background: C.surfaceAlt}}>
                {error && (
                    <Stack direction="row" alignItems="center" gap="8px" sx={{marginBottom: '6px'}}>
                        <Typography sx={{fontSize: 11, color: C.coral, flex: 1}}>{error}</Typography>
                        <ButtonBase
                            onClick={() => { setError(''); inputRef.current?.focus(); }}
                            sx={{
                                border: `1px solid ${C.coral}33`,
                                background: C.coralL,
                                color: C.coral,
                                borderRadius: '8px',
                                padding: '2px 8px',
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                flexShrink: 0,
                            }}>
                            Dismiss
                        </ButtonBase>
                    </Stack>
                )}
                <Box ref={varMenuRef} sx={{position: 'relative'}}>
                    {/* Var autocomplete dropdown */}
                    {varMenuOpen && filteredVars.length > 0 && (
                        <Box data-var-list sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            marginBottom: '4px',
                            background: C.surface,
                            border: `1px solid ${C.border}`,
                            borderRadius: '10px',
                            boxShadow: `0 4px 16px ${C.shadow}`,
                            padding: '4px',
                            maxHeight: 200,
                            overflowY: 'auto',
                            zIndex: 10,
                        }}>
                            {filteredVars.map((v, i) => (
                                <ButtonBase
                                    key={v.name}
                                    onMouseDown={(e) => { e.preventDefault(); insertVar(v.name); }}
                                    sx={{
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        padding: '6px 10px',
                                        borderRadius: '7px',
                                        fontFamily: 'inherit',
                                        fontSize: 12,
                                        cursor: 'pointer',
                                        background: i === varMenuIndex ? `${typeConfig.color}11` : 'transparent',
                                        '&:hover': {background: `${typeConfig.color}11`},
                                    }}>
                                    <Stack direction="row" alignItems="center" gap="8px" sx={{width: '100%', minWidth: 0}}>
                                        <Typography sx={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            color: typeConfig.color,
                                            flexShrink: 0,
                                        }}>
                                            {`{{${v.name}}}`}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: 11,
                                            color: C.textMuted,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {v.sample}
                                        </Typography>
                                    </Stack>
                                </ButtonBase>
                            ))}
                        </Box>
                    )}

                    {/* Input + send */}
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        gap="8px"
                        sx={{
                            border: `1px solid ${C.border2}`,
                            borderRadius: '14px',
                            background: C.surface,
                            padding: '4px 4px 4px 12px',
                            '&:focus-within': {borderColor: `${typeConfig.color}66`},
                            transition: 'border-color .15s ease',
                        }}>
                        <InputBase
                            inputRef={inputRef}
                            multiline
                            minRows={1}
                            maxRows={5}
                            value={input}
                            onChange={handleInputChange}
                            onKeyDown={handleInputKeyDown}
                            onBlur={() => setTimeout(() => setVarMenuOpen(false), 150)}
                            placeholder={vars.length ? 'Message — type {{ for vars' : 'Ask anything about this field'}
                            sx={{
                                flex: 1,
                                fontFamily: 'inherit',
                                fontSize: 13,
                                lineHeight: 1.5,
                                padding: '6px 0',
                                color: C.ink,
                                '& textarea': {resize: 'none'},
                            }}
                        />
                        <ButtonBase
                            onClick={() => send()}
                            disabled={!canSend}
                            aria-label="Send message"
                            sx={{
                                width: 36,
                                height: 36,
                                flexShrink: 0,
                                borderRadius: '10px',
                                background: canSend ? typeConfig.color : C.surfaceAlt,
                                color: canSend ? '#fff' : C.textMuted,
                                cursor: canSend ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background .15s ease, color .15s ease',
                            }}>
                            {loading ? (
                                <Spinner />
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                            )}
                        </ButtonBase>
                    </Stack>
                </Box>
            </Box>
        </Stack>
    );
}
